import { MuseumApp } from "./app";
import { loadExhibitionRegistry } from "./asset-loader";
import type { ExhibitionRegistry } from "./contracts";
import {
  disposeBackdrop,
  renderExhibitionMenu,
  renderRegistryFailure,
  renderUnknownExhibition,
} from "./exhibition-menu";
import { BRAND, copyFor, openingFor, readLanguage, storeLanguage, type Language } from "./i18n";
import { travel, wipe } from "./transition";
import { readExhibitionSelection, readRegion, regionUrl, selectionUrl } from "./selection";
import type { RegionId } from "./regions";

export class MuseumShell {
  readonly #root: HTMLElement;
  #registry: ExhibitionRegistry | null = null;
  #app: MuseumApp | null = null;
  #activeSlug: string | null = null;
  #focusSlug: string | null = null;

  constructor(root: HTMLElement) {
    this.#root = root;
    window.addEventListener("popstate", () => this.#route());
  }

  async start(): Promise<void> {
    this.#renderLoading();
    try {
      this.#registry = await loadExhibitionRegistry();
      this.#route();
    } catch {
      const copy = this.#copy();
      renderRegistryFailure(this.#root, copy, () => void this.start());
    }
  }

  #route(force = false): void {
    const registry = this.#registry;
    if (registry === null) return;
    const selection = readExhibitionSelection();
    if (
      !force &&
      this.#app !== null &&
      selection.kind === "exhibition" &&
      selection.slug === this.#activeSlug
    ) {
      return;
    }
    this.#disposeViewer();
    const copy = this.#copy();
    if (selection.kind === "menu") {
      const region = readRegion();
      // Named for the heritage on screen rather than for the site, so a bookmark or a
      // second tab says which one it is.
      document.title = `${openingFor(copy, region).title} — ${BRAND}`;
      renderExhibitionMenu(
        this.#root,
        registry,
        readLanguage(),
        region,
        copy,
        {
          onLanguage: (language) => this.#changeLanguage(language),
          onRegion: (region) => this.#changeRegion(region),
          onSelect: (slug) => this.#select(slug),
        },
        this.#focusSlug,
      );
      this.#focusSlug = null;
      return;
    }
    if (selection.kind === "invalid") {
      renderUnknownExhibition(this.#root, copy, () => this.#backToMenu());
      return;
    }
    const exhibition = registry.exhibitions.find((candidate) => candidate.slug === selection.slug);
    if (exhibition === undefined) {
      renderUnknownExhibition(this.#root, copy, () => this.#backToMenu());
      return;
    }
    this.#focusSlug = exhibition.slug;
    this.#activeSlug = exhibition.slug;
    this.#app = new MuseumApp(this.#root, exhibition, () => this.#backToMenu());
    void this.#app.start();
  }

  // Both directions tear down one page and build another, so both get the curtain. Only
  // the deliberate navigations do: popstate and language changes re-route without one,
  // because a wipe on every back button would read as a stutter rather than a passage.
  #select(slug: string): void {
    void wipe(() => {
      history.pushState(null, "", selectionUrl(slug));
      this.#route();
    });
  }

  #backToMenu(): void {
    void wipe(() => {
      history.pushState(null, "", selectionUrl(null));
      this.#route();
    });
  }

  // Moving between heritages replaces every photograph on the page at once. That gets
  // its own passage rather than the exhibition curtain: the destination is named while
  // the swap happens behind it, so the change reads as arrival rather than as a flash.
  #changeRegion(region: RegionId): void {
    const name = this.#copy().regionNames[region];
    void travel(name, () => {
      history.pushState(null, "", regionUrl(region));
      this.#route();
    });
  }

  #changeLanguage(language: Language): void {
    storeLanguage(language);
    this.#route(true);
  }

  #copy() {
    const copy = copyFor(readLanguage());
    document.documentElement.lang = copy.locale;
    document.documentElement.dir = copy.direction;
    return copy;
  }

  #disposeViewer(): void {
    // Releases the menu's WebGL context before the viewer asks for its own.
    disposeBackdrop();
    this.#app?.dispose();
    this.#app = null;
    this.#activeSlug = null;
    this.#root.replaceChildren();
  }

  #renderLoading(): void {
    const copy = this.#copy();
    this.#root.innerHTML = `
      <main class="exhibition-index exhibition-index--message">
        <section class="panel"><p role="status">${copy.preparing}</p></section>
      </main>`;
  }
}
